import styled, { css } from 'styled-components';

/**
 * The button.
 *
 * There were forty-two of these, defined separately in twenty-four files. Every
 * page invented its own padding, radius, font size and hover, so no two screens
 * agreed on what a button looks like — and twenty-three of them filled with the
 * brand gradient, which meant a screen like Approvals had three "primary"
 * buttons competing at once. A primary action that appears three times is not a
 * primary action.
 *
 * So: one component, four variants, and a rule about how they are spent.
 *
 *   primary    solid accent. The single most likely next action in a region.
 *              If you want two, one of them is secondary.
 *   secondary  outlined and quiet. The default for anything that is not the
 *              one obvious action.
 *   ghost      no border. For dense rows and toolbars where a box per control
 *              would be more furniture than function.
 *   danger     quiet until hovered, then red. Destructive actions should be
 *              easy to find and hard to hit by accident.
 *
 * No gradients. The gradient is the brand mark, and a mark that appears on
 * every button stops being a mark. No lift-on-hover either: a control that
 * moves when you approach it is harder to click, and thirty of them moving on
 * one page is noise.
 */

const SIZES = {
  sm: css`
    padding: 0.35rem 0.7rem;
    font-size: var(--text-xs);
    gap: 0.3rem;
  `,
  md: css`
    padding: 0.5rem 0.9rem;
    font-size: var(--text-sm);
    gap: 0.4rem;
  `,
  lg: css`
    padding: 0.65rem 1.15rem;
    font-size: var(--text-base);
    gap: 0.45rem;
  `
};

const VARIANTS = {
  primary: css`
    background: var(--accent-cyan);
    color: #04121a;
    border-color: transparent;

    &:not(:disabled):hover { filter: brightness(1.08); }
  `,
  secondary: css`
    background: transparent;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:not(:disabled):hover {
      color: var(--text-primary);
      border-color: var(--border-strong);
      background: var(--surface-hover);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--text-secondary);
    border-color: transparent;

    &:not(:disabled):hover {
      color: var(--text-primary);
      background: var(--surface-hover);
    }
  `,
  danger: css`
    background: transparent;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:not(:disabled):hover {
      color: var(--error-color);
      border-color: rgba(248, 113, 113, 0.5);
      background: var(--error-dim);
    }
  `,
  /* Solid red, for the confirmation step of something irreversible - never for
     the control that merely opens that confirmation. */
  destructive: css`
    background: var(--error-color);
    color: #1a0505;
    border-color: transparent;

    &:not(:disabled):hover { filter: brightness(1.08); }
  `
};

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font-family: inherit;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast),
              color var(--transition-fast), filter var(--transition-fast);

  ${(p) => SIZES[p.$size] || SIZES.md}
  ${(p) => VARIANTS[p.$variant] || VARIANTS.secondary}

  /* A full-width button is a deliberate choice, not what happens when a
     toolbar runs out of room. Buttons never stretch to fill a flex row. */
  ${(p) => (p.$full ? 'width: 100%;' : 'flex: 0 0 auto;')}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/** A square button holding only an icon. Needs a label for screen readers. */
export const IconButton = styled(Button).attrs({ $variant: 'ghost' })`
  padding: 0;
  width: ${(p) => (p.$size === 'sm' ? '26px' : '32px')};
  height: ${(p) => (p.$size === 'sm' ? '26px' : '32px')};
`;

/**
 * A row of buttons.
 *
 * Exists so a card's actions cannot end up stretched edge to edge or wrapped
 * mid-label, which is what `flex: 1` on every button produced: "Run Now" broke
 * across two lines while its neighbours sat half empty.
 */
export const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

export default Button;
